import React, { Fragment, useState, useRef } from 'react';
import Accordion from 'syf-component-library/ui/patterns/Accordion/Accordion';
import { captureClickAnalyticsObject } from '../../utils/analytics/analyticsDispatchers';
import { useAppDispatch } from '../../store/store';
import { renderAccountDetails } from '../../store/accountListSlice';
import { setSelectedAccountDetails } from '../../store/paymentTokenSlice';
import StyledComponentManager from '../styledComponentManager/StyledComponentManager';
import AccountTitle from './accountTitle/AccountTitle';
import AccordionBody from './AccordionBody';
import { detectDeviceType } from '../../utils/helpers';

const AccountAccordion = (props: any) => {
  const { accounts } = props;
  const dispatch = useAppDispatch();
  const openAccordionHeaderRef = useRef<HTMLDivElement | null>(null);
  const [accordionExpansionTracker, setAccordionExpansionTracker] = useState<{
    [key: string]: boolean;
  }>({});

  //This part is to suppress the validateDOMNesting error shown in the console by the slideInModal
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args[0].includes('validateDOMNesting')) {
      return;
    }
    originalConsoleError(...args);
  };

  const handleAccordionClick = (isAccordionOpen: any, account: any) => {
    const { accountId } = account;
    /**
     * A timer must be set when collapsing the accordion,
     * as we're resetting it to account details from a single-use card.
     * This prevents the account details from appearing before the accordion collapse transition complete.
     */
    const timeOutValue = isAccordionOpen ? 0 : 380;
    /**
     * Setting up initialize params for add to wallet SDK
     * @button id set params and bind the click event to initAppleWallet window
     * @props holds the account detail and user details
     */
    // Show account details section on click
    const previousAccountId = Object.keys(accordionExpansionTracker)[0];
    // flip the showAccountDetails flag to previous selection to false
    if (previousAccountId && previousAccountId !== accountId) {
      setTimeout(
        () =>
          dispatch(
            renderAccountDetails({
              accountId: previousAccountId,
              renderAccountDetailsDisplayFlag: false, // showAccountDetails flag is flipped to false for previously opened accordion
            }),
          ),
        400, // needs to be same as the accordion Transition time
      );
    }

    setTimeout(
      () =>
        dispatch(
          renderAccountDetails({
            accountId,
            renderAccountDetailsDisplayFlag: true, // showAccountDetails flag is flipped to true
          }),
        ),
      timeOutValue,
    );

    /**
     * updates the details in paymentTokenSlice
     * sets the account details if already present
     * or sets the default values (empty) if not already generated
     * delay the state update by a 0.6 (slightly larger than accordion transition time which is set to 0.4 seconds)
     * so when the user navigates from one accordion to another they do not see newly selected account
     * on the old accordion - single use card section
     * hence the setTimeout for 0.6 secs aka 600 ms
     */
    setTimeout(
      () =>
        dispatch(
          setSelectedAccountDetails({
            accountId,
          }),
        ),
      600, // needs to be slightly higher than accordion transition time (400)
    );
    setAccordionExpansionTracker(() => ({
      [account.accountId]: !accordionExpansionTracker[account.accountId],
    }));

    if (isAccordionOpen) {
      captureClickAnalyticsObject({
        dataObject: 'button',
        dataReason: `dropdown:${account.displayName}`,
        dataType: detectDeviceType,
      });
    }
  };

  return (
    <>
      {accounts.map((account: any, index: number) => (
        <Fragment key={`${account.accountId}`}>
          <StyledComponentManager>
            <Accordion
              header={
                <AccountTitle
                  headerRef={index === 0 ? openAccordionHeaderRef : null}
                  {...account}
                  isAccordionCollapsed={
                    !accordionExpansionTracker[account.accountId]
                  }
                />
              }
              body={
                <AccordionBody
                  account={account}
                  index={index}
                  isAccordionCollapsed={
                    !accordionExpansionTracker[account.accountId]
                  }
                />
              }
              transitionDuration={0.4}
              id={account.accountId}
              className="accordion-accounts"
              forceClose={!accordionExpansionTracker[account.accountId]}
              headerClickCallBack={(isAccordionOpen) => {
                handleAccordionClick(isAccordionOpen, account);
              }}
              data-testid="accountsAccordion"
            />
          </StyledComponentManager>
        </Fragment>
      ))}
    </>
  );
};

export default AccountAccordion;
